<template>
  <div class="checklist-picker">
    <dropdown
      ref="dropdownRef"
      title="Checklist Picker"
      @open="handleDropdownOpen()"
    >
      <slot name="button">
        <dd-icon-button icon="plus" type="circle" size="small" color="green" />
      </slot>
      <template #content>
        <div>
          <sleek-filter-input
            v-if="showFilterInput"
            ref="sleekFilterInputRef"
            v-model="filterQuery"
            placeholder="Search by name…"
            show-small-text
          />
          <ul>
            <li
              v-for="option in filteredOptions"
              :key="option.id"
              @click="handleSelection(option.id)"
            >
              <span>
                {{ option.name }}
              </span>
            </li>
          </ul>
        </div>
      </template>
    </dropdown>
    <new-checklist-modal
      v-model:visible="showNewChecklistModal"
      :project-id="projectId"
      :item-id="itemId"
      :is-linked-to-task="isLinkedToTask"
      @checklist-created="(checklist) => $emit('checklist-created', checklist)"
    />
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue'
import { IChecklist, IChecklistTemplate } from '@/api/types/response.types'
import SearchableDropdownMixin from '@/mixins/searchable-dropdown.mixin'
import InternalProjectsAPI from '@/api/modules/internal-projects.api'
import RouteAssistant from '@/helpers/route-assistant'

export default defineComponent({
  name: 'ChecklistPicker',

  extends: SearchableDropdownMixin,

  props: {
    projectId: {
      type: Number,
      required: true,
    },
    itemId: {
      type: Number,
      required: true,
    },
    isLinkedToTask: {
      type: Boolean,
      default: true,
    },
  },

  emits: ['checklist-created'],

  data() {
    return {
      checklistDropDownOptions: [
        { id: -2, name: '+ New Checklist', canFilter: false },
      ] as { id: number; name: string; canFilter: boolean }[],
      showNewChecklistModal: false,
    }
  },

  computed: {
    totalOptionsCount(): number {
      return this.checklistDropDownOptions.length - 1
    },
    filteredOptions(): { id: number; name: string; canFilter: boolean }[] {
      if (this.filterQuery.length === 0) {
        return this.checklistDropDownOptions
      }

      return this.checklistDropDownOptions.filter(option => {
        return (
          !option.canFilter ||
          option.name.toLowerCase().includes(this.filterQuery.toLowerCase())
        )
      })
    },
  },

  async created() {
    if (this.$permissions.isAdminOrOwner) {
      this.checklistDropDownOptions.push({
        id: -1,
        name: '+ New Template',
        canFilter: false,
      })
    }

    const checklistTemplatesReponse = await InternalProjectsAPI.get_ChecklistTemplates(
      this.projectId,
    )

    const checklistTemplates = checklistTemplatesReponse.filter(
      (template: IChecklistTemplate) => template.statusID === null,
    )

    checklistTemplates.forEach((template: IChecklistTemplate) => {
      this.checklistDropDownOptions.push({
        id: template.id,
        name: template.title,
        canFilter: true,
      })
    })
  },

  methods: {
    async handleSelection(selectedID: number) {
      if (selectedID === -2) {
        this.showNewChecklistModal = true
      } else if (selectedID === -1) {
        if (this.isLinkedToTask) {
          this.$router.push(
            RouteAssistant.getProjectSettingsChecklistTemplateRoute(
              this.$route.params.accountId,
              this.projectId,
            ),
          )
        } else {
          this.$router.push(
            RouteAssistant.getMailboxSettingsChecklistTemplateRoute(
              this.$route.params.accountId,
              this.projectId,
            ),
          )
        }
      } else {
        await InternalProjectsAPI.post_ChecklistTemplateAddToTask(
          this.projectId,
          selectedID,
          this.itemId,
        ).then((checklist: IChecklist) => {
          this.$emit('checklist-created', checklist)
        })
      }
      const dropdown = this.$refs.dropdownRef as any
      dropdown.handleClose()
    },
  },
})
</script>

<style lang="scss">
</style>
